<template>
  <div class="about-page">
    <!-- ===================== HERO ===================== -->
    <header class="kl-hero">
      <div class="kl-hero__decor" aria-hidden="true"></div>
      <div class="kl-hero__inner">
        <span class="kl-hero__eyebrow">KeyLearning</span>
        <h1 class="kl-hero__title">{{ $t("home.aboutUsView.whoWeAre") }}</h1>
        <p class="kl-hero__lead">
          {{ $t("home.aboutUsView.whoWeAreDescription") }}
        </p>
      </div>
    </header>

    <!-- ===================== PROPÓSITO ===================== -->
    <section class="kl-section kl-section--purpose">
      <div class="kl-shell kl-shell--narrow text-center">
        <h2 class="kl-title">{{ $t("home.aboutUsView.purpose") }}</h2>
        <span class="kl-title-accent kl-title-accent--center"></span>
        <p class="kl-lead mt-4">
          {{ $t("home.aboutUsView.purposeDescription") }}
        </p>
      </div>
    </section>

    <!-- ===================== MISIÓN / VISIÓN ===================== -->
    <section class="kl-section pt-0">
      <div class="kl-shell">
        <div class="kl-pillars">
          <article class="kl-pillar kl-pillar--mission">
            <h3 class="kl-pillar__title">
              {{ $t("home.aboutUsView.mission") }}
            </h3>
            <p class="kl-pillar__text">
              {{ $t("home.aboutUsView.missionDescription") }}
            </p>
          </article>
          <article class="kl-pillar kl-pillar--vision">
            <h3 class="kl-pillar__title">
              {{ $t("home.aboutUsView.vision") }}
            </h3>
            <p class="kl-pillar__text">
              {{ $t("home.aboutUsView.visionDescription") }}
            </p>
          </article>
        </div>
      </div>
    </section>

    <!-- ===================== EMPRESA ===================== -->
    <section class="kl-section kl-section--company">
      <div class="kl-shell">
        <div class="kl-company-head">
          <span class="kl-company-eyebrow">Key Learning SpA</span>
          <span class="kl-title-accent kl-title-accent--center"></span>
        </div>
        <article class="kl-company-card">
          <div class="kl-company-row">
            <span class="kl-company-row__badge">
              <b-icon icon="building"></b-icon>
            </span>
            <div class="kl-richtext" style="text-align: justify">
              <p>
                <strong>{{ $t("home.aboutUsView.keyLearning") }}</strong>
                <RenderVHtml
                  class="d-inline p-0"
                  :content="
                    $t('home.aboutUsView.companyDescriptionP1', {
                      company: '',
                      institutions: `<strong>${$t(
                        'home.aboutUsView.higherEducationInstitutions'
                      )}</strong>`,
                      technologies: `<strong>${$t(
                        'home.aboutUsView.technologies'
                      )}</strong>`,
                      qualities: `<strong>${$t(
                        'home.aboutUsView.qualities'
                      )}</strong>`,
                    })
                  "
                ></RenderVHtml>
              </p>
            </div>
          </div>

          <div class="kl-company-row">
            <span class="kl-company-row__badge">
              <b-icon icon="cpu-fill"></b-icon>
            </span>
            <div class="kl-richtext" style="text-align: justify">
              <p>
                <RenderVHtml
                  class="d-inline p-0"
                  :content="
                    $t('home.aboutUsView.companyDescriptionP2', {
                      aiTool: `<strong>${$t(
                        'home.aboutUsView.aiTool'
                      )}</strong>`,
                      curriculumTasks: `<strong>${$t(
                        'home.aboutUsView.curriculumTasks'
                      )}</strong>`,
                      analysis: `<strong>${$t(
                        'home.aboutUsView.analysis'
                      )}</strong>`,
                    })
                  "
                ></RenderVHtml>
              </p>
            </div>
          </div>

          <div class="kl-company-row">
            <span class="kl-company-row__badge">
              <b-icon icon="people-fill"></b-icon>
            </span>
            <div class="kl-richtext" style="text-align: justify">
              <p>
                <RenderVHtml
                  class="d-inline p-0"
                  :content="
                    $t('home.aboutUsView.companyDescriptionP3', {
                      company: `<strong>${$t(
                        'home.aboutUsView.keyLearning'
                      )}</strong>`,
                      professionals: `<strong>${$t(
                        'home.aboutUsView.professionals'
                      )}</strong>`,
                      educationalModels: `<strong>${$t(
                        'home.aboutUsView.educationalModels'
                      )}</strong>`,
                      curriculumInnovations: `<strong>${$t(
                        'home.aboutUsView.curriculumInnovations'
                      )}</strong>`,
                    })
                  "
                ></RenderVHtml>
              </p>
            </div>
          </div>

          <div class="kl-company-row">
            <span class="kl-company-row__badge">
              <b-icon icon="patch-check-fill"></b-icon>
            </span>
            <div class="kl-richtext" style="text-align: justify">
              <p>
                <RenderVHtml
                  class="d-inline p-0"
                  :content="
                    $t('home.aboutUsView.companyDescriptionP4', {
                      enhanceCapacity: `<strong>${$t(
                        'home.aboutUsView.enhanceCapacity'
                      )}</strong>`,
                    })
                  "
                ></RenderVHtml>
              </p>
            </div>
          </div>
        </article>
      </div>
    </section>

    <!-- ===================== EQUIPO ===================== -->
    <section class="kl-section academic-team-section">
      <div class="kl-shell text-center">
        <h2 class="kl-title">{{ $t("home.aboutUsView.team") }}</h2>
        <span class="kl-title-accent kl-title-accent--center"></span>
      </div>

      <div class="team-container-premium">
        <!-- Leadership Section -->
        <div class="team-category">
          <div class="members-grid leadership-grid">
            <div class="member-item">
              <div class="member-avatar team-img-1"></div>
              <div class="member-content">
                <h4 class="member-fullname">
                  Jaime Alejandro<br />Torrealba Cubillos
                </h4>
                <div class="role-badge ceo-badge">CEO</div>
                <p class="member-title">Founding Partner</p>
                <a
                  href="https://www.linkedin.com/in/jtc797/"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="member-linkedin"
                  aria-label="LinkedIn de Jaime Torrealba"
                >
                  <b-icon icon="linkedin"></b-icon>
                </a>
              </div>
            </div>

            <div class="member-item">
              <div class="member-avatar team-img-2"></div>
              <div class="member-content">
                <h4 class="member-fullname">
                  Ernesto Lazaro<br />Figueredo Escobar
                </h4>
                <div class="role-badge academic-badge">Academic Director</div>
                <p class="member-title">Founding Partner</p>
                <a
                  href="https://www.linkedin.com/in/ernesto-figueredo-36332b1b9/"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="member-linkedin"
                  aria-label="LinkedIn de Ernesto Figueredo"
                >
                  <b-icon icon="linkedin"></b-icon>
                </a>
              </div>
            </div>

            <div class="member-item">
              <div class="member-avatar team-img-3"></div>
              <div class="member-content">
                <h4 class="member-fullname">Andrés<br />Henriquez Pérez</h4>
                <div class="role-badge cto-badge">CTO</div>
                <p class="member-title">Founding Partner</p>
                <a
                  href="https://www.linkedin.com/in/andres-henriquez-perez/?locale=es"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="member-linkedin"
                  aria-label="LinkedIn de Andrés Henríquez"
                >
                  <b-icon icon="linkedin"></b-icon>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div class="team-category mt-5">
          <div class="members-grid leadership-grid">
            <div class="member-item">
              <div class="member-avatar team-img-5"></div>
              <div class="member-content">
                <h4 class="member-fullname">
                  Roldán Giancarlo<br />Zegarra Velásquez
                </h4>
                <div class="role-badge dev-badge">Lead Backend Developer</div>
                <a
                  href="https://www.linkedin.com/in/giancarlo-zegarra/"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="member-linkedin"
                  aria-label="LinkedIn de Roldán Zegarra"
                >
                  <b-icon icon="linkedin"></b-icon>
                </a>
              </div>
            </div>

            <div class="member-item">
              <div class="member-avatar team-img-4"></div>
              <div class="member-content">
                <h4 class="member-fullname">
                  Franco Antonio<br />Muñoz Ibarra
                </h4>
                <div class="role-badge dev-badge">Lead Fullstack Developer</div>
                <a
                  href="https://cl.linkedin.com/in/franco-muñoz-ibarra/"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="member-linkedin"
                  aria-label="LinkedIn de Franco Muñoz"
                >
                  <b-icon icon="linkedin"></b-icon>
                </a>
              </div>
            </div>

            <div class="member-item">
              <div class="member-avatar team-img-6"></div>
              <div class="member-content">
                <h4 class="member-fullname">
                  Nicolás Ignacio<br />Osorio Isami
                </h4>
                <div class="role-badge dev-badge">Lead Frontend Developer</div>
                <a
                  href="https://www.linkedin.com/in/nicolas-osorio-isami/"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="member-linkedin"
                  aria-label="LinkedIn de Nicolás Osorio"
                >
                  <b-icon icon="linkedin"></b-icon>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <ContactForm></ContactForm>
    <ContactBanner></ContactBanner>
  </div>
</template>

<script>
// import * as names from "@/store/names";
// import { mapGetters } from "vuex";
// import { generateUniqueId } from "@/utils/utils";
export default {
  name: "AboutUsView",
  components: {
    ContactForm: () => import("@/components/new-page/ContactForm"),
    ContactBanner: () => import("@/components/new-page/ContactBanner"),
    RenderVHtml: () => import("@/components/reusable/RenderVHtml"),
  },
  props: {
    // allows_crud: {
    //   type: Boolean,
    // },
  },
  data() {
    return {};
  },
  computed: {
    // ...mapGetters({}),
  },
  methods: {},
  mounted() {},
  created() {},
};
</script>

<style scoped>
/* ============================================================
   KeyLearning · Sistema de diseño de página pública
   Reutilizable entre vistas (AboutUsView, InstitutionsView, ...)
   ============================================================ */
.about-page {
  /* Migrado al sistema global de tokens (utils/styles_kl.css).
     Se conservan los nombres locales para no tocar todas las reglas de abajo;
     ahora sus valores derivan del estándar global. */
  --kl-blue: var(--kl-color-primary);
  --kl-blue-dark: var(--kl-color-primary-deep);
  --kl-teal: var(--kl-color-secondary);
  --kl-ink: var(--kl-text-heading);
  --kl-text: var(--kl-text-body);
  --kl-muted: var(--kl-text-muted);
  --kl-bg: var(--kl-bg-page);
  --kl-card: var(--kl-surface);
  --kl-border: var(--kl-border-soft);
  --kl-shadow: var(--kl-shadow-lg);
  --kl-shadow-hover: var(--kl-shadow-lg-hover);

  background: var(--kl-bg);
  color: var(--kl-text);
  font-family: inherit;
  overflow-x: hidden;
}

/* ---------- Primitivas reutilizables ---------- */
.kl-shell {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 var(--kl-space-6);
}
.kl-shell--narrow {
  max-width: 820px;
}
.kl-section {
  padding: 90px 0;
}
.kl-section.pt-0 {
  padding-top: 0;
}
.kl-title {
  font-weight: var(--kl-fw-bold);
  font-size: var(--kl-fs-title-fluid);
  line-height: var(--kl-lh-tight);
  color: var(--kl-ink);
  margin: 0;
  letter-spacing: var(--kl-ls-tight);
}
.kl-title-accent {
  display: block;
  width: 64px;
  height: 5px;
  margin-top: 18px;
  border-radius: var(--kl-radius-pill);
  background: linear-gradient(90deg, var(--kl-blue) 0%, var(--kl-teal) 100%);
}
.kl-title-accent--center {
  margin-left: auto;
  margin-right: auto;
}
.kl-lead {
  font-size: var(--kl-fs-body-lg);
  line-height: var(--kl-lh-relaxed);
  color: var(--kl-text);
  margin: 0;
}

/* ---------- Hero ---------- */
.kl-hero {
  position: relative;
  background: linear-gradient(
    130deg,
    var(--kl-blue-dark) 0%,
    var(--kl-blue) 55%,
    var(--kl-teal) 115%
  );
  color: #ffffff;
  padding: 96px 32px 110px;
  border-bottom-right-radius: 48px;
  overflow: hidden;
}
.kl-hero__decor {
  position: absolute;
  top: -120px;
  right: -80px;
  width: 460px;
  height: 460px;
  border-radius: 50%;
  background: radial-gradient(
    circle at center,
    rgba(255, 255, 255, 0.16) 0%,
    rgba(255, 255, 255, 0) 70%
  );
  pointer-events: none;
}
.kl-hero__inner {
  position: relative;
  z-index: 1;
  max-width: 1180px;
  margin: 0 auto;
}
.kl-hero__eyebrow {
  display: inline-block;
  font-size: var(--kl-fs-small);
  font-weight: var(--kl-fw-bold);
  letter-spacing: var(--kl-ls-wider);
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.85);
  padding: 7px 16px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: var(--kl-radius-pill);
  margin-bottom: 22px;
}
.kl-hero__title {
  font-weight: var(--kl-fw-bold);
  font-size: var(--kl-fs-hero);
  line-height: var(--kl-lh-tight);
  margin: 0 0 22px;
  letter-spacing: var(--kl-ls-tight);
}
.kl-hero__lead {
  max-width: 100%;
  font-size: var(--kl-fs-body-lg);
  line-height: var(--kl-lh-relaxed);
  color: rgba(255, 255, 255, 0.92);
  margin: 0;
  text-align: justify;
}

/* ---------- Pilares (Misión / Visión) ---------- */
.kl-pillars {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 36px;
}
.kl-pillar {
  position: relative;
  background: var(--kl-card);
  border: 1px solid var(--kl-border);
  border-radius: 22px;
  padding: 56px 38px 40px;
  box-shadow: var(--kl-shadow);
  transition: transform 0.35s ease, box-shadow 0.35s ease;
  overflow: hidden;
}
.kl-pillar::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background: linear-gradient(90deg, var(--kl-blue) 0%, var(--kl-teal) 100%);
}
.kl-pillar:hover {
  transform: translateY(-8px);
  box-shadow: var(--kl-shadow-hover);
}
.kl-pillar__title {
  font-weight: var(--kl-fw-bold);
  font-size: var(--kl-fs-title-md);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--kl-blue-dark);
  margin: 0 0 14px;
}
.kl-pillar__text {
  font-size: var(--kl-fs-body-lg);
  line-height: var(--kl-lh-relaxed);
  color: var(--kl-text);
  margin: 0;
  text-align: justify;
}

/* ---------- Empresa (rejilla de tarjetas) ---------- */
.kl-section--company {
  background: var(--kl-surface);
}
.kl-company-head {
  text-align: center;
  margin-bottom: 48px;
}
.kl-company-eyebrow {
  display: inline-block;
  font-size: var(--kl-fs-small);
  font-weight: var(--kl-fw-bold);
  letter-spacing: var(--kl-ls-wider);
  text-transform: uppercase;
  color: var(--kl-blue-dark);
  padding: 7px 18px;
  border: 1px solid var(--kl-border);
  border-radius: var(--kl-radius-pill);
  background: linear-gradient(
    135deg,
    rgba(0, 110, 200, 0.06),
    rgba(1, 173, 183, 0.06)
  );
}
.kl-company-head .kl-title-accent {
  margin-top: 16px;
}

.kl-company-card {
  position: relative;
  background: var(--kl-card);
  border: 1px solid var(--kl-border);
  border-radius: 22px;
  padding: 48px 44px;
  box-shadow: var(--kl-shadow);
  overflow: hidden;
}
.kl-company-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background: linear-gradient(90deg, var(--kl-blue) 0%, var(--kl-teal) 100%);
}
.kl-company-row {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 28px 0;
  border-bottom: 1px solid var(--kl-border);
}
.kl-company-row:first-child {
  padding-top: 0;
}
.kl-company-row:last-child {
  padding-bottom: 0;
  border-bottom: none;
}
.kl-company-row__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 54px;
  height: 54px;
  border-radius: 16px;
  background: linear-gradient(135deg, var(--kl-blue) 0%, var(--kl-teal) 100%);
  color: #ffffff;
  font-size: 24px;
  box-shadow: 0 10px 24px rgba(0, 110, 200, 0.28);
}
.kl-company-card .kl-richtext p {
  margin-bottom: 0;
}
.kl-richtext {
  font-size: var(--kl-fs-body-lg);
  line-height: var(--kl-lh-relaxed);
  color: var(--kl-text);
}
.kl-richtext ::v-deep strong {
  color: var(--kl-blue-dark);
}
.kl-richtext p {
  margin-bottom: 16px;
}

/* ---------- Equipo ---------- */
.academic-team-section {
  background: var(--kl-bg);
}
.team-container-premium {
  max-width: 1180px;
  margin: 0 auto;
  padding: 56px 32px 0;
}
.team-category {
  margin-bottom: 0;
}

/* Leadership Grid */
.members-grid {
  display: grid;
  gap: 36px;
}
.leadership-grid {
  grid-template-columns: repeat(3, 1fr);
}
.member-item {
  text-align: center;
  background: var(--kl-surface);
  border-radius: 22px;
  padding: 40px 28px 32px;
  border: 1px solid var(--kl-border);
  box-shadow: var(--kl-shadow);
  transition: transform 0.35s ease, box-shadow 0.35s ease,
    border-color 0.35s ease;
  display: flex;
  flex-direction: column;
}
.member-item:hover {
  border-color: var(--kl-color-primary);
  transform: translateY(-8px);
  box-shadow: var(--kl-shadow-hover);
}
.member-avatar {
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  margin: 0 auto 25px;
  border: 5px solid #f0f0f0;
  transition: transform 0.35s ease, border-color 0.35s ease,
    box-shadow 0.35s ease;
  position: relative;
}
.member-item:hover .member-avatar {
  border-color: var(--kl-color-primary);
  transform: scale(1.08);
  box-shadow: 0 6px 6px rgba(0, 110, 200, 0.25);
}
.member-content {
  padding: 0 15px;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.member-fullname {
  font-weight: var(--kl-fw-bold);
  font-size: var(--kl-fs-title-md);
  line-height: 1.4;
  color: var(--kl-text-heading);
  margin: 0 0 15px 0;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.role-badge {
  display: inline-block;
  padding: 8px 20px;
  border-radius: 20px;
  font-weight: var(--kl-fw-bold);
  font-size: var(--kl-fs-caption);
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 12px;
  color: var(--kl-text-on-primary);
}
.ceo-badge,
.academic-badge,
.cto-badge,
.dev-badge {
  background: linear-gradient(
    135deg,
    var(--kl-color-primary) 0%,
    var(--kl-color-secondary) 100%
  );
}
.member-title {
  font-weight: var(--kl-fw-semibold);
  font-size: var(--kl-fs-body);
  color: var(--kl-text-muted);
  margin: 0 0 5px 0;
}
.member-linkedin {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-top: auto;
  border-radius: 50%;
  background: #0a66c2;
  color: #ffffff;
  font-size: 20px;
  text-decoration: none;
  opacity: 0;
  transition: opacity 0.35s ease, transform 0.35s ease, background 0.3s ease,
    box-shadow 0.3s ease;
}
.member-item:hover .member-linkedin {
  opacity: 1;
  transform: translateY(0);
}
.member-linkedin:hover {
  background: #004182;
  transform: translateY(-3px) scale(1.1);
  box-shadow: 0 2px 2px rgba(10, 102, 194, 0.35);
  color: #ffffff;
}

/* Imágenes del equipo */
.team-img-1 {
  background-image: url(../../assets/new-page/Jaime_Torrealba_Cubillos.jpg);
}
.team-img-2 {
  background-image: url(../../assets/new-page/Ernesto_Figueredo.jpg);
}
.team-img-3 {
  background-image: url(../../assets/new-page/Andres_Henriquez_Perez.jpeg);
}
.team-img-4 {
  background-image: url(../../assets/new-page/Franco_Muñoz.png);
  /* Sube el encuadre para que no se corte la cabeza (ajusta el % si hace falta) */
  background-position: center 20%;
}

.team-img-5 {
  background-image: url(../../assets/new-page/Roldán_Zegarra.png);
  /* Sube el encuadre para que no se corte la cabeza (ajusta el % si hace falta) */
  background-position: center 10%;
}

.team-img-6 {
  background-image: url(../../assets/new-page/Nicolas_Osorio.png);
  /* background-image: url(../../assets/new-page/Nicolas_Osorio.jpg); */
  /* Placeholder - agregar imagen de Nicolas */
}
/* ============================================================
   Responsive
   ============================================================ */
@media (max-width: 992px) {
  .kl-company-card {
    padding: 40px 32px;
  }
  .leadership-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .kl-section {
    padding: 64px 0;
  }
  .kl-hero {
    padding: 72px 24px 84px;
    border-bottom-right-radius: 32px;
  }
  .kl-shell {
    padding: 0 22px;
  }
  .kl-pillars {
    grid-template-columns: 1fr;
    gap: 28px;
  }
  .leadership-grid {
    grid-template-columns: 1fr;
    max-width: 420px;
    margin: 0 auto;
  }
  .team-container-premium {
    padding: 44px 22px 0;
  }
  .member-linkedin {
    opacity: 1;
    transform: translateY(0);
  }
  .member-avatar {
    width: 180px;
    height: 180px;
  }
}

@media (max-width: 480px) {
  .kl-hero__lead,
  .kl-pillar__text,
  .kl-lead {
    text-align: left;
  }
  .kl-company-card {
    padding: 30px 24px;
  }
  .kl-company-row {
    flex-direction: column;
    gap: 16px;
    padding: 24px 0;
  }
  .member-avatar {
    width: 150px;
    height: 150px;
  }
  .member-fullname {
    font-size: 19px;
    min-height: 52px;
  }
}
</style>